import React from "react"
import TextField from "material-ui/TextField"
import strings from "../../../localization/strings"
import ChooseHierarchyChip from "../../../components/hierarchy/choose_hierarchy/ChooseHierarchyChip"
import autobind from "class-autobind"
import AccountStore from "../../../stores/AccountStore"
import * as equipmentHelper from "../../../helpers/equipmentHelper"
import MapLoader from "../../../components/map/MapLoader"
import * as equipmentActions from "../../../actions/EquipmentActions"
import * as userActions from "../../../actions/UserActions"
import * as hierarchyHelper from "../../../helpers/hierarchyHelper"
import { IEquipmentDocument, IUserDocument } from "../../../models"
import ClientLocalDateTimeStr from "../../../components/common/ClientLocalDateTimeStr"

interface IProps {
    equipment: IEquipmentDocument
    readonly: boolean
}
interface IState {
    name: string
    externalId: string
    defaultHierarchyItemId: string
    externalIdErrorText: string
    nameErrorText: string
    lastPunchEmployee: IUserDocument
}

export default class EquipmentForm extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props)
        this.state = {
            name: props.equipment.Name,
            externalId: props.equipment.ExternalId,
            defaultHierarchyItemId: props.equipment.DefaultHierarchyItemId,
            externalIdErrorText: "",
            nameErrorText: "",
            lastPunchEmployee: null
        }
        autobind(this)
    }

    componentDidMount() {
        this.getLastPunchEmployee()
    }

    componentWillReceiveProps(nextProps) {
        this.setState({
            name: nextProps.equipment.Name,
            externalId: nextProps.equipment.ExternalId,
            defaultHierarchyItemId: nextProps.equipment.DefaultHierarchyItemId
        })
    }

    getLastPunchEmployee() {
        const { equipment } = this.props
        if (equipment.LastKnownEquipmentMetaData && equipment.LastKnownEquipmentMetaData.EmployeeId) {
            userActions.loadUser(equipment.LastKnownEquipmentMetaData.EmployeeId).then((result) => {
                this.setState({ lastPunchEmployee: result.data })
            })
        }
    }

    saveEquipment() {
        const { name, externalId, defaultHierarchyItemId } = this.state
        const equipment = Object.assign({}, this.props.equipment)
        equipment.Name = name.trim()
        equipment.ExternalId = externalId.trim()
        equipment.DefaultHierarchyItemId = defaultHierarchyItemId

        this.checkValidate(equipment).then((result) => {
            if (result) {
                equipmentActions.updateEquipment(equipment)
            }
        })
    }

    checkValidate(equipment) {
        let isValidate = true
        if (this.state.name.trim().length === 0) {
            this.setState({
                nameErrorText: strings.Name_cannot_be_empty
            })
            isValidate = false
        } else {
            this.setState({
                nameErrorText: ""
            })
        }

        if (this.state.externalId.trim().length === 0) {
            this.setState({
                externalIdErrorText: strings.External_ID_cannot_be_empty
            })
            isValidate = false
        } else {
            this.setState({
                externalIdErrorText: ""
            })
        }

        if (this.props.equipment.ExternalId === equipment.ExternalId) {
            return new Promise(function (resolve, reject) {
                resolve(isValidate)
            })
        }

        const accId = AccountStore.getCurrentAccount().Id
        return equipmentActions.equipmentExternalIdExists(accId, equipment.ExternalId).then((result) => {
            if (result.data) {
                this.setState({
                    externalIdErrorText: strings.External_ID_must_be_unique
                })
                isValidate = false
            }
            return isValidate
        })
    }

    handleNameChange(e) {
        this.setState(
            {
                name: e.target.value
            },
            () => {
                this.saveEquipment()
            }
        )
    }

    handleExternalIdChange(e) {
        this.setState(
            {
                externalIdErrorText: "",
                externalId: e.target.value
            },
            () => {
                this.saveEquipment()
            }
        )
    }

    onHierarchySave(levelId) {
        this.setState(
            {
                defaultHierarchyItemId: levelId
            },
            () => {
                this.saveEquipment()
            }
        )
    }

    getLevelLabel(selectedLevelId) {
        if (selectedLevelId) {
            const levelName = hierarchyHelper.getHierarchyItemNameByHierarchyId(selectedLevelId)
            if (!levelName) return ""
            const showLevelName = levelName.length < 20 ? levelName : levelName.substr(0, 19) + "..."
            return (
                <span>
                    <b>{hierarchyHelper.getHierarchyDefinitionNameByHierarchyId(selectedLevelId)}</b> {showLevelName}
                </span>
            )
        } else if (AccountStore.getCurrentAccount()) {
            return (
                <span>
                    <b>{strings.Account}</b> {AccountStore.getCurrentAccount().Name}
                </span>
            )
        } else {
            return ""
        }
    }

    render() {
        const { name, externalId, lastPunchEmployee } = this.state
        const { equipment } = this.props
        const currentLocation = equipment.LastKnownEquipmentMetaData ? this.getLevelLabel(equipment.LastKnownEquipmentMetaData.HierarchyItemId) : ""
        const locationVerificationEmployee = lastPunchEmployee ? lastPunchEmployee.FirstName + " " + lastPunchEmployee.LastName : ""
        const lastPunchLocation =
            equipment.LastKnownEquipmentMetaData && equipment.LastKnownEquipmentMetaData.Latitude
                ? {
                    lat: equipment.LastKnownEquipmentMetaData.Latitude,
                    lng: equipment.LastKnownEquipmentMetaData.Longitude
                }
                : null
        const lastPunchLocationLabel =
            equipment.LastKnownEquipmentMetaData && equipment.LastKnownEquipmentMetaData.Latitude
                ? `${equipment.LastKnownEquipmentMetaData.Latitude}, ${equipment.LastKnownEquipmentMetaData.Longitude}`
                : ""
        return (
            <div className="EquipmentForm" style={{ minWidth: 400 }}>
                <div style={{ padding: 20 }}>
                    <div
                        style={{
                            height: 72,
                            display: "inline-block",
                            verticalAlign: "top",
                            width: "35%"
                        }}
                    >
                        <TextField
                            id="name"
                            autoFocus={true}
                            floatingLabelText={strings.Name}
                            defaultValue={name}
                            onChange={this.handleNameChange}
                            errorText={this.state.nameErrorText}
                            style={{ width: "100%", marginRight: 20 }}
                            disabled={this.props.readonly}
                        />
                    </div>
                    <div
                        style={{
                            height: 72,
                            display: "inline-block",
                            verticalAlign: "top",
                            width: "45%",
                            marginLeft: 20
                        }}
                    >
                        <TextField
                            id="externalId"
                            floatingLabelText={strings.External_ID}
                            defaultValue={externalId}
                            onChange={this.handleExternalIdChange}
                            errorText={this.state.externalIdErrorText}
                            style={{ width: "100%", marginRight: 20 }}
                            maxLength="15"
                            disabled={this.props.readonly}
                        />
                    </div>
                    <div style={{ height: 72, display: "flex", width: "100%" }}>
                        <div
                            style={{
                                marginTop: 14,
                                display: "inline-block",
                                verticalAlign: "top",
                            }}
                        >
                            <ChooseHierarchyChip
                                readonly={this.props.readonly}
                                chipLabel={strings.Home_Hierarchy}
                                hierarchyItemId={this.state.defaultHierarchyItemId}
                                handleSelectHierarchy={this.onHierarchySave}
                                getClickableAccessHierarchies={() => equipmentHelper.getEquipmentAccessHierarchyIds()}
                                getAccessRight={() => true}
                            />
                        </div>
                        <div
                            style={{
                                marginTop: 14,
                                display: "inline-block",
                                verticalAlign: "top",
                                width: "60%",
                                marginLeft: 20
                            }}
                        >
                            <div>
                                <div
                                    style={{
                                        width: 110,
                                        display: "inline-block"
                                    }}
                                >
                                    {strings.Current_Hierarchy}:{" "}
                                </div>
                                <div style={{ display: "inline-block" }}>{currentLocation}</div>
                            </div>
                            <div>
                                <div
                                    style={{
                                        width: 110,
                                        display: "inline-block"
                                    }}
                                >
                                    {strings.Last_Time}:{" "}
                                </div>
                                <div style={{ display: "inline-block" }}>
                                    {equipment.LastKnownEquipmentMetaData && equipment.LastKnownEquipmentMetaData.LocationDateTime &&
                                        <ClientLocalDateTimeStr
                                            hierarchyId={equipment.LastKnownEquipmentMetaData.HierarchyItemId}
                                            jobId={equipment.LastKnownEquipmentMetaData.JobId}
                                            utcTime={equipment.LastKnownEquipmentMetaData.LocationDateTime}
                                            formatStr="MM/DD/YYYY h:mma"
                                        />
                                    }
                                </div>
                            </div>
                            <div>
                                <div
                                    style={{
                                        width: 110,
                                        display: "inline-block"
                                    }}
                                >
                                    {strings.Employee}:{" "}
                                </div>
                                <span>{locationVerificationEmployee}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    {equipment.LastKnownEquipmentMetaData && equipment.LastKnownEquipmentMetaData.Latitude ?
                        <div style={{ height: 195, marginTop: 20 }}>
                            <div style={{ paddingLeft: 20 }}>{lastPunchLocationLabel}</div>
                            <MapLoader locations={[lastPunchLocation]} />
                        </div> :
                        equipment.LastKnownEquipmentMetaData ?
                            <div style={{ paddingLeft: 20 }}>{strings.The_last_punch_was_added_manually}</div> :
                            <div style={{ paddingLeft: 20 }}>{strings.No_known_metadata_for_this_equipment}</div>
                    }
                </div>
            </div>
        )
    }
}
